import React, { useState, useRef, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';
import { useNotifications } from '../../context/NotificationContext';
import NotificationList from './NotificationList';
import './NotificationBell.css';

// Categories that are internal (attorney-only) and should not show to clients
const INTERNAL_CATEGORIES = [
  'assignment',
  'recall',
  'transfer',
  'approval',
  'internal_request',
  'new_filing',
  'filing_queue'
];

// Title patterns that indicate internal notifications
const INTERNAL_TITLE_PATTERNS = [
  'recalled',
  'attorney assigned',
  'transferred',
  'assignment',
  'new filing ready',
  'filing ready'
];

/**
 * NotificationBell Component
 * Bell icon with unread count badge and dropdown
 */
function NotificationBell() {
  const { t } = useTranslation('notifications');
  const { unreadCount, notifications, markAllAsRead } = useNotifications();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  const location = useLocation();

  // Detect if we're in the attorney dashboard
  const isAttorneyDashboard = location.pathname.startsWith('/attorney');
  const viewAllLink = isAttorneyDashboard ? '/attorney/client-messages' : '/dashboard#messages';

  // Filter out internal notifications for client view
  const clientNotifications = useMemo(() => {
    return notifications.filter(notification => {
      // Check category
      if (INTERNAL_CATEGORIES.includes(notification.category)) {
        return false;
      }

      // Check title patterns
      const title = (notification.title || '').toLowerCase();
      for (const pattern of INTERNAL_TITLE_PATTERNS) {
        if (title.includes(pattern)) {
          return false;
        }
      }

      return true;
    });
  }, [notifications]);

  // Calculate client-visible unread count
  const clientUnreadCount = useMemo(() => {
    return clientNotifications.filter(n => !n.read).length;
  }, [clientNotifications]);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const toggleDropdown = () => {
    setIsOpen(!isOpen);
  };

  const handleMarkAllRead = async (e) => {
    e.stopPropagation();
    await markAllAsRead();
  };

  return (
    <div className="notification-bell-container" ref={dropdownRef}>
      <button
        className="notification-bell-button"
        onClick={toggleDropdown}
        aria-label={t('notifications.bell.label', 'Notifications')}
        aria-haspopup="true"
        aria-expanded={isOpen}
      >
        <svg
          className="notification-bell-icon"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
          <path d="M13.73 21a2 2 0 0 1-3.46 0" />
        </svg>
        {clientUnreadCount > 0 && (
          <span className="notification-badge" aria-label={`${clientUnreadCount} unread`}>
            {clientUnreadCount > 99 ? '99+' : clientUnreadCount}
          </span>
        )}
      </button>

      {isOpen && (
        <div className="notification-dropdown">
          <div className="notification-dropdown-header">
            <h3>{t('notifications.title', 'Notifications')}</h3>
            {clientUnreadCount > 0 && (
              <button
                className="mark-all-read-button"
                onClick={handleMarkAllRead}
              >
                {t('notifications.markAllRead', 'Mark all as read')}
              </button>
            )}
          </div>

          <div className="notification-dropdown-content">
            {clientNotifications.length === 0 ? (
              <div className="notification-empty">
                <p>{t('notifications.empty', 'No notifications')}</p>
              </div>
            ) : (
              <NotificationList
                notifications={clientNotifications.slice(0, 5)}
                onNotificationClick={() => setIsOpen(false)}
              />
            )}
          </div>

          <div className="notification-dropdown-footer">
            <Link to={viewAllLink} onClick={() => setIsOpen(false)}>
              {t('notifications.viewAll', 'View all notifications')}
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}

export default NotificationBell;
